import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MuiModal from '@mui/material/Modal';
import { StyledModalInternal, StyledCloseButtonWrapper } from './Modal.styles';
import { SVGIcon } from '../../BuildingBlocks/SVGIcon';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

/**
 * Modal component is used for Signing in and any othe location a pop up modal is needed
 */
function Modal(props) {
  const { bgColor, width, height, handleClose, open, className, onRenderedHook, children, closeButton, showClose } =
    props;
  const isMounted = useRef(false);
  const scrollPos = useRef(0);

  const disableScroll = (newOpenValue) => {
    if (newOpenValue) {
      scrollPos.current = window.scrollY || document.documentElement.scrollTop;
      document.body.classList.add('no-scrolling');
      document.body.style.top = `-${scrollPos.current}px`;
    } else {
      document.body.classList.remove('no-scrolling');
      window.scrollTo(0, scrollPos.current);
    }
  };

  useEffect(() => {
    disableScroll(open);

    return () => disableScroll(false);
  }, []);

  useEffect(() => {
    if (isMounted && open === false) {
      disableScroll(false);
    }
    isMounted.current = true;
  }, [open]);

  const renderCloseButton = () =>
    showClose ? (
      <StyledCloseButtonWrapper
        className="modal-close"
        onClick={() => {
          handleClose();
          disableScroll(false);
        }}
      >
        {closeButton}
      </StyledCloseButtonWrapper>
    ) : null;

  return (
    <MuiModal
      onClose={() => {
        handleClose();
        disableScroll(false);
      }}
      onRendered={() => {
        onRenderedHook();
        disableScroll(true);
      }}
      open={open}
      className={className}
    >
      <StyledModalInternal width={width} height={height} bgColor={bgColor}>
        {renderCloseButton()}
        {children}
      </StyledModalInternal>
    </MuiModal>
  );
}

Modal.propTypes = {
  /**
   * Background color of the Modal
   */
  bgColor: PropTypes.string,

  /**
   * The Items to appear inside the Modal
   */
  children: ReactChildrenPropType.isRequired,

  /**
   * Prop for passing classname for styling Modal with Styled Components
   */
  className: PropTypes.string,

  /**
   * The close button prop
   */
  closeButton: PropTypes.node,

  /**
   * Function to handle the closing of the modal
   */
  handleClose: PropTypes.func.isRequired,

  /**
   * The Max height of the Desktop Modal
   */
  height: PropTypes.string,

  /**
   * Interaction/analytics hooks
   */
  onRenderedHook: PropTypes.func,

  /**
   * Boolean for if the Modal is open or closed
   */
  open: PropTypes.bool.isRequired,

  /**
   * Prop to hide/show thw close button on the modal
   */
  showClose: PropTypes.bool,

  /**
   * The Max width of the Desktop Modal
   */
  width: PropTypes.string
};

Modal.defaultProps = {
  bgColor: '#ffffff',
  className: undefined,
  closeButton: <SVGIcon selectedIcon="closeModal" />,
  height: null,
  onRenderedHook: () => {},
  showClose: true,
  width: null
};

export default Modal;
